/**
 * Office.js helpers for inserting slides into the current PowerPoint presentation.
 */

/**
 * Inserts slides from a base64-encoded PPTX into the current presentation.
 * @param base64Pptx - Base64-encoded PPTX file content
 * @param sourceSlideIds - Optional array of source slide IDs to insert.
 *                         If omitted, all slides are inserted.
 */
export async function insertSlides(
  base64Pptx: string,
  sourceSlideIds?: string[]
): Promise<void> {
  const targetSlideId = await getTargetSlideId();
  const options: PowerPoint.InsertSlideOptions = {};
  if (sourceSlideIds && sourceSlideIds.length > 0) {
    options.sourceSlideIds = sourceSlideIds;
  }
  if (targetSlideId) {
    options.targetSlideId = targetSlideId;
  }

  await PowerPoint.run(async context => {
    context.presentation.insertSlidesFromBase64(base64Pptx, options);
    await context.sync();
  });
}

/**
 * Converts an ArrayBuffer to a base64 string.
 *
 * Uses a chunked approach to avoid O(n²) string concatenation — processing
 * 32 KB at a time keeps intermediate string allocations manageable for large
 * PPTX files.
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunks: string[] = [];
  const chunkSize = 0x8000; // 32 KB
  for (let i = 0; i < bytes.length; i += chunkSize) {
    // String.fromCharCode.apply is safe for chunks up to ~64 KB; 32 KB is well
    // within that limit.
    chunks.push(
      String.fromCharCode.apply(
        null,
        bytes.subarray(i, i + chunkSize) as unknown as number[]
      )
    );
  }
  return btoa(chunks.join(''));
}

async function getTargetSlideId(): Promise<string | undefined> {
  try {
    return await PowerPoint.run(async context => {
      const selectedSlides = context.presentation.getSelectedSlides();
      selectedSlides.load('items/id');
      await context.sync();

      const selectedSlideId =
        selectedSlides.items[selectedSlides.items.length - 1]?.id;
      if (selectedSlideId) {
        return selectedSlideId;
      }

      const allSlides = context.presentation.slides;
      allSlides.load('items/id');
      await context.sync();

      return allSlides.items[allSlides.items.length - 1]?.id;
    });
  } catch (error) {
    console.warn('PowerPoint target slide resolution failed', {
      message: error instanceof Error ? error.message : String(error),
    });
    return undefined;
  }
}
